/**
 * Explore drawer: slide the Insights + Timeframe sections out of view while the
 * EGM list loads, and back in when the cards arrive (display-only).
 *
 * Without this the sections only faded to opacity 0 while loading and kept their
 * layout, leaving a large blank band at the top of the drawer with the skeleton
 * cards pushed down to where the list normally starts. Now the whole header block
 * (title + Insight pills + Timeframe pills) slides up, fades and collapses its
 * height, so the skeleton cards move up to fill the space; when the data lands the
 * block slides back in while the real cards fade in.
 *
 * This file is the pure part: timing constants + the "should it be collapsed"
 * decision. It deliberately does NOT read or write the scroll auto-hide state
 * (`isDrawerFiltersVisible`): the loading collapse is a separate, derived layer on
 * top of it, so scrolling and loading can never fight each other and the scroll
 * state is exactly what it was once loading ends. It never touches the insight
 * data path, sorting, API params, caches or response shapes.
 *
 * CSS lives in src/styles/_modules/sub-modules/results-toolbar.scss and mirrors
 * these numbers (guarded by exploreSections.contract.test.ts).
 */
import { SKELETON_APPEAR_DELAY_MS } from './exploreSkeleton';

/** Slide/collapse time, both directions (ease-out). */
export const SECTIONS_MOTION_MS = 240;
/** The block drifts this far up while it fades out (and starts this far up when it returns). */
export const SECTIONS_SLIDE_OFFSET_PX = 16;
/**
 * Loads that finish faster than this never animate the sections out and in. It is
 * the skeleton's own appear delay: the sections collapse exactly when the skeleton
 * becomes visible, so a quick / cached load changes nothing on screen.
 */
export const SECTIONS_COLLAPSE_DELAY_MS = SKELETON_APPEAR_DELAY_MS;

/**
 * `<html>` class set by the post-login Random Outcomes drawer while the Explore
 * cards are held back (RandomOutcomesDrawer). The sections stay tucked away for
 * that window too, and slide in when the class is swapped for the card reveal.
 */
export const LOGIN_CARDS_HOLD_CLASS = 'ro-cards-hold';

/**
 * True while the Insight + Timeframe sections should be slid out.
 *
 *  - `showDrawer`: the Explore drawer is up (nothing to collapse otherwise).
 *  - `skeletonVisible`: the loading skeleton has faded in (loading lasted longer than
 *    SECTIONS_COLLAPSE_DELAY_MS) and has not started leaving yet. Covers the first
 *    load, every timeframe / insight change and any other list reload.
 *  - `loginCardsHeld`: login hand-off from the Random Outcomes drawer; the cards are
 *    held invisible, so the sections wait with them.
 */
export function shouldCollapseExploreSections(input: {
  showDrawer: boolean;
  skeletonVisible: boolean;
  loginCardsHeld: boolean;
}): boolean {
  if (!input.showDrawer) return false;
  return input.skeletonVisible || input.loginCardsHeld;
}
